import React, { useRef, useState, useEffect } from 'react';
import { Checkbox, FormControlLabel, FormHelperText, FormControl, useTheme } from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { generateTestId } from 'app/functions';
import HtmlContent from 'components/html/HtmlContent';
import { useSelector } from 'react-redux';
import { spacing } from 'common/theme';

export const CheckboxHot = ({
  name,
  control,
  label,
  html,
  required = false,
  helperText,
  linksNotClickedErrorText,
  rules = {},
  sx,
  testID,
  value: externalValue,
  onChange: externalOnChange,
  onAllLinksClicked,
  error: externalError,
  ...rest
}) => {
  const htmlContentRef = useRef();
  const [linksStatus, setLinksStatus] = useState({ isComplete: true, totalLinks: 0 });
  const text = useSelector((state) => state.config.Translations);
  const [fieldError, setFieldError] = useState('');
  const [status, setStatus] = useState({});
  const theme = useTheme();
  const hasAutoCompletedRef = useRef(false);
  const initialValueRef = useRef(null);

  const isControlled = !!control;
  const isExternalControlled = externalValue !== undefined && externalOnChange !== undefined;
  
  const watchedValue = isControlled && control ? useWatch({ control, name }) : undefined;
  
  useEffect(() => {
    if (isControlled && initialValueRef.current === null && watchedValue !== undefined) {
      const normalizedValue = !!watchedValue || watchedValue === 1 || watchedValue === '1';
      initialValueRef.current = normalizedValue;
    }
  }, [isControlled, watchedValue]);

  if (required && !rules.required) {
    rules.required = 'שדה חובה';
  }

  const customRules = {
    ...rules,
    validate: {
      ...(rules.validate || {}),
      linksClicked: (value) => {
        const normalizedValue = !!value || value === 1 || value === '1';
        if (!normalizedValue) return true;
        if (linksStatus.totalLinks === 0) return true;
        const isInitialTrueValue = !!initialValueRef.current;
        if (isInitialTrueValue && hasAutoCompletedRef.current) return true;
        return linksStatus.isComplete || linksNotClickedErrorText || text['agreement_link_not_clicked_error'];
      }
    }
  };

  const handleLinkStatusChange = (status, skipCallback = false) => {
    setStatus(status);
    const clickedAllLinks = status.clickedLinks.length === status.allLinks.length;
    setLinksStatus({...status, isComplete: clickedAllLinks});
    
    if (clickedAllLinks && isControlled) {
      setFieldError('');
    }
    
    if (clickedAllLinks && onAllLinksClicked && !skipCallback) {
      onAllLinksClicked();
    }
  };

  const content = html || label || '';
  const displayError = externalError || fieldError;
  const hasLinks = linksStatus.totalLinks > 0;

  if (isControlled) {
    return (
      <Controller
        name={name}
        control={control}
        rules={customRules}
        render={({ field: { value, onChange, ...field }, fieldState: { error } }) => {
          const handleLinkStatusChangeWithValue = (linkStatus) => {
            const normalizedValue = !!value || value === 1 || value === '1';
            const isInitialTrueValue = !!initialValueRef.current;
            const hasLinks = linkStatus.totalLinks > 0;
            const noLinksClicked = linkStatus.clickedLinks.length === 0;
            const shouldAutoComplete = normalizedValue && hasLinks && noLinksClicked;
            
            if (shouldAutoComplete) {
              if (!hasAutoCompletedRef.current) {
                hasAutoCompletedRef.current = true;
              }
              const allLinksArray = Array.from(linkStatus.allLinks || []);
              handleLinkStatusChange({
                ...linkStatus,
                clickedLinks: allLinksArray,
                clickedCount: allLinksArray.length,
                isComplete: true
              }, isInitialTrueValue);
            } else {
              if (!normalizedValue) {
                hasAutoCompletedRef.current = false;
              }
              handleLinkStatusChange(linkStatus);
            }
          };

          return (
          <React.Fragment>
            <FormControl 
              error={!!displayError || !!error} 
              required={required}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                flexDirection: "row",
                gap: 0,
                ...sx
              }}
            >
              <FormControlLabel
                sx={{ 
                  margin: 0,
                  marginRight: 0,
                  '& .MuiFormControlLabel-label': {
                    marginLeft: 0,
                    marginRight: 0,
                  }
                }}
                control={
                  <Checkbox
                    testid={generateTestId(testID, `${name}-checkbox`)}
                    checked={!!value || value === 1 || value === '1'}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (!linksStatus.isComplete && hasLinks) {
                        setFieldError(linksNotClickedErrorText || text['agreement_link_not_clicked_error']);
                      } else {
                        onChange(checked);
                        setFieldError('');
                      }
                    }}
                    {...field}
                    {...rest}
                  />
                }
                label=""
              />
              <HtmlContent 
                className='hot-checkbox-label'
                ref={htmlContentRef}
                content={content}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onLinkStatusChange={handleLinkStatusChangeWithValue}
              />
            </FormControl>
            <FormHelperText 
              sx={{
                marginTop: "-5px",
                marginLeft: "40px",
                minHeight: "1rem",
                color: theme.palette.text.error,
                fontSize: "0.75rem",
                visibility: (!!error?.message || displayError || helperText || ((!!value || value === 1 || value === '1') && hasLinks && !linksStatus.isComplete)) ? 'visible' : 'hidden'
              }}
            >
              { displayError || error?.message || helperText || ' ' }
            </FormHelperText>
          </React.Fragment>
          );
        }}
      />
    );
  }

  const [internalValue, setInternalValue] = useState(false);
  const checked = isExternalControlled ? externalValue : internalValue;

  const handleUncontrolledChange = (checked) => {
    if (!linksStatus.isComplete && linksStatus.totalLinks > 0) {
      setFieldError(linksNotClickedErrorText || text['agreement_link_not_clicked_error']);
      return;
    }
    
    setFieldError('');
    setInternalValue(checked);
    if (externalOnChange) {
      externalOnChange(checked);
    }
  };

  const handleLinkStatusChangeUncontrolled = (linkStatus) => {
    const normalizedChecked = !!checked || checked === 1 || checked === '1';
    const shouldAutoComplete = normalizedChecked && linkStatus.totalLinks > 0 && linkStatus.clickedLinks.length === 0;
    if (shouldAutoComplete) {
      const allLinksArray = Array.from(linkStatus.allLinks || []);
      handleLinkStatusChange({
        ...linkStatus,
        clickedLinks: allLinksArray,
        clickedCount: allLinksArray.length,
        isComplete: true
      }, true);
    } else {
      handleLinkStatusChange(linkStatus);
    }
  };

  return (
    <React.Fragment>
      <FormControl 
        error={!!displayError} 
        required={required}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          flexDirection: "row",
          gap: 0,
          ...sx
        }}
      >
        <FormControlLabel
          sx={{ 
            margin: 0,
            marginRight: 0,
            '& .MuiFormControlLabel-label': {
              marginLeft: 0,
              marginRight: 0,
            }
          }}
          control={
            <Checkbox
              testid={generateTestId(testID, `${name}-checkbox`)}
              checked={!!checked || checked === 1 || checked === '1'}
              onChange={(e) => handleUncontrolledChange(e.target.checked)}
              {...rest}
            />
          }
          label=""
        />
        <HtmlContent 
          className='hot-checkbox-label'
          ref={htmlContentRef}
          content={content}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onLinkStatusChange={handleLinkStatusChangeUncontrolled}
        />
      </FormControl>
      <FormHelperText 
        sx={{
          marginTop: "-5px",
          marginLeft: "40px",
          minHeight: "1rem",
          color: theme.palette.text.error,
          fontSize: "0.75rem",
          visibility: (displayError || helperText || ((!!checked || checked === 1 || checked === '1') && hasLinks && !linksStatus.isComplete)) ? 'visible' : 'hidden'
        }}
      >
        { displayError || helperText || ' ' }
      </FormHelperText>
    </React.Fragment>
  );
};

export default CheckboxHot;

